import clsx from "clsx";
import { format } from "date-fns";

import { getEventColor, getEventIcon } from "../utils/events";
import { Icon } from "./base/Icon";
import { Text, View } from "./base";
import { JsonViewer } from "./base/JsonViewer";
import { AgentSessionEvent } from "../redux/reducers/agentSessionEventSlice";
import { TurnLatencyTooltip } from "./TurnLatencyTooltip";

type AgentSessionTranscriptProps = {
  events: AgentSessionEvent[];
};

const filterAndSortEvents = (events: AgentSessionEvent[]) => {
  return (
    events
      .filter((event) => !event.name.startsWith("metrics."))
      // Filter out metrics/latency events from transcript view
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
  );
};

const formatTimestamp = (date: Date | string) => {
  try {
    return format(new Date(date), "HH:mm:ss");
  } catch {
    return "";
  }
};

// Reformat events to have a role and content property for backwards compatibility.
// All new event transcripts are built from transcript_part.
const getFormattedEvent = (event: AgentSessionEvent, hasTranscriptParts: boolean = false) => {
  if (!hasTranscriptParts && event?.name === "text") {
    return {
      ...event,
      name: "transcript_part",
      data: {
        role: "user",
        content: event.data.text,
      },
    };
  }

  if (!hasTranscriptParts && event?.name === "text_to_speech") {
    return {
      ...event,
      name: "transcript_part",
      data: {
        role: "assistant",
        content: event.data.text,
      },
    };
  }

  return event;
};

const getEventTypeKey = (event: AgentSessionEvent) => {
  if (event.name === "transcript_part") {
    return event.name;
  }

  return "system";
};

// Extract latency data for a specific turn_end event
const getLatencyData = (events: AgentSessionEvent[], targetEvent: AgentSessionEvent) => {
  const latencies: Record<string, number> = {};
  let duration: number | undefined;

  // Get duration and turn number from turn_end event
  if (targetEvent.name === "turn_end") {
    duration = (targetEvent.data as any)?.duration;
    const targetTurn = (targetEvent.data as any)?.turn;

    // If we have a turn number, look for latency events with the same turn
    if (targetTurn !== undefined) {
      for (const event of events) {
        const eventTurn = (event.data as any)?.turn;

        // Match events by turn number
        if (eventTurn === targetTurn && event.name?.startsWith("metrics.latency.")) {
          const key = event.name.replace("metrics.latency.", "");
          const seconds = (event.data as any)?.value_s ?? (event.data as any)?.latency_s ?? (event.data as any)?.value;
          const ms = typeof seconds === "number" ? Math.round(seconds * 1000) : undefined;
          if (ms !== undefined) {
            latencies[key] = ms;
          }
        }
      }
    }
  }

  return { latencies, duration };
};

export const AgentSessionTranscript = ({ events }: AgentSessionTranscriptProps) => {
  const eventsToShow = filterAndSortEvents(events);
  const hasTranscriptParts = eventsToShow.some((event) => event.name === "transcript_part");

  if (eventsToShow.length === 0) {
    return (
      <View className="flex-1 flex items-center justify-center">
        <Text className="text-sm text-gray-500 dark:text-gray-400 italic">No transcript events available</Text>
      </View>
    );
  }

  return (
    <View className="flex flex-col flex-1">
      {eventsToShow.map((event, index) => {
        const currentEvent = getFormattedEvent(event, hasTranscriptParts);
        const currentType = getEventTypeKey(currentEvent);
        const nextEvent = getFormattedEvent(eventsToShow[index + 1], hasTranscriptParts);
        const nextType = nextEvent ? getEventTypeKey(nextEvent) : null;
        const timestamp = formatTimestamp(currentEvent.timestamp || currentEvent.createdAt);
        const isLastOfType = currentType !== nextType;

        // Special handling for turn_end events with latency tooltips
        if (currentEvent.name === "turn_end") {
          const { latencies, duration } = getLatencyData(events, currentEvent);
          return (
            <View
              key={index}
              className="flex flex-col items-center mb-2"
            >
              <TurnLatencyTooltip
                latencies={latencies}
                duration={duration}
                turnNumber={(currentEvent.data as any)?.turn}
              >
                <View className="flex flex-row items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors">
                  <Icon
                    name="clock"
                    size={14}
                    className="text-gray-500 dark:text-gray-400"
                  />
                  <Text className="text-xs text-gray-600 dark:text-gray-400">Turn End</Text>
                </View>
              </TurnLatencyTooltip>
            </View>
          );
        }

        if (currentEvent.name === "transcript_part") {
          return (
            <View
              key={index}
              className="flex flex-col items-start"
            >
              <View
                className={`flex flex-col mb-1 p-2 rounded-lg max-w-[66%] min-w-[33%] ${
                  currentEvent.data?.role === "assistant"
                    ? "bg-indigo-100 dark:bg-indigo-900 self-end items-end"
                    : "bg-gray-200 dark:bg-gray-700 mr-8 self-start"
                }`}
              >
                <View className="flex flex-row items-center mb-1">
                  <Icon
                    name={currentEvent.data?.role === "assistant" ? "bot" : "user"}
                    size={16}
                    className={`mr-2 ${currentEvent.data?.role === "assistant" ? "text-indigo-600 dark:text-indigo-400" : "text-gray-600 dark:text-gray-400"}`}
                  />
                  <Text className="text-xs font-medium">{currentEvent.data?.role === "assistant" ? "Assistant" : "User"}</Text>
                </View>
                <Text className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">{currentEvent.data.content}</Text>
              </View>
              {isLastOfType && timestamp && (
                <View className={`mb-3 flex flex-row items-center gap-1 ${currentEvent.data?.role === "assistant" ? "self-end" : "self-start"}`}>
                  <Icon
                    name="clock"
                    size={12}
                    className="text-gray-400 dark:text-gray-500"
                  />
                  <Text className="text-[11px] text-gray-400 dark:text-gray-500">{timestamp}</Text>
                </View>
              )}
            </View>
          );
        } else {
          // System events
          let isLastSystem = true;
          for (let i = index + 1; i < eventsToShow.length; i++) {
            if (getEventTypeKey(eventsToShow[i]) === "system") {
              isLastSystem = false;
              break;
            }
            if (getEventTypeKey(eventsToShow[i]) !== "system") {
              break;
            }
          }

          // Check if this is a background event
          const isBackground = currentEvent.data?.background === true;
          const backgroundTaskName = currentEvent.data?.background_task_name;

          return (
            <View
              key={index}
              className="flex flex-col items-center"
            >
              <View
                className={clsx(
                  "flex flex-col items-center justify-center mb-1 p-2 rounded-lg self-center max-w-[50%] min-w-[50%]",
                  isBackground ? "bg-orange-100 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-700" : "bg-gray-100 dark:bg-gray-700",
                )}
              >
                <View className="flex flex-row mb-1 items-center justify-center">
                  <Icon
                    name={getEventIcon(currentEvent.name, currentEvent.data)}
                    size={16}
                    className={clsx(
                      currentEvent.name === "log" || currentEvent.name === "transfer_call" || currentEvent.name === "merge_call" ? "mr-2" : "mr-1",
                      getEventColor(currentEvent.name, "input"),
                    )}
                  />
                  {isBackground && (
                    <Icon
                      name="clock"
                      size={14}
                      className="text-orange-500 dark:text-orange-400 mr-1"
                    />
                  )}
                  <Text className="text-xs font-medium capitalize">{currentEvent.name.replace(/_/g, " ")}</Text>
                </View>
                {isBackground && backgroundTaskName && (
                  <Text className="text-xs text-orange-600 dark:text-orange-400 font-medium mb-1">Background: {backgroundTaskName}</Text>
                )}
                {currentEvent.name === "start" && <Text className="text-xs text-gray-500">User started the call</Text>}
                {currentEvent.name === "interrupt" && <Text className="text-xs text-orange-600 dark:text-orange-400">Turn interrupted</Text>}
                {currentEvent.name === "audio" && <Text className="text-xs text-gray-500">{currentEvent.data.path}</Text>}
                {currentEvent.name === "silence" && <Text className="text-xs text-gray-500">{currentEvent.data.duration} ms of silence</Text>}
                {currentEvent.name === "error" && (
                  <JsonViewer
                    data={currentEvent.data.message}
                    className="self-start"
                    maxDepth={2}
                  />
                )}
                {currentEvent.name === "session_failed" && (
                  <JsonViewer
                    data={currentEvent.data.error}
                    className="self-start"
                    maxDepth={2}
                  />
                )}
                {(currentEvent.name === "transfer_call" || currentEvent.name === "merge_call") && (
                  <Text className="text-xs text-gray-500">
                    {currentEvent.name === "transfer_call" ? "Call transferred" : "Call merged"}
                    {currentEvent.data.phone_number && ` to ${currentEvent.data.phone_number}`}
                  </Text>
                )}
                {currentEvent.name === "log" && (
                  <View className="flex flex-col items-start w-full">
                    {currentEvent.data.location && (
                      <Text className="text-xs text-gray-500 dark:text-gray-400 font-mono mb-1">📍 {currentEvent.data.location}</Text>
                    )}
                    <JsonViewer
                      data={currentEvent.data.message}
                      className="self-start"
                      maxDepth={2}
                    />
                    {currentEvent.data.attributes && Object.keys(currentEvent.data.attributes).length > 0 && (
                      <JsonViewer
                        data={currentEvent.data.attributes}
                        className="self-start mt-1"
                        maxDepth={3}
                      />
                    )}
                  </View>
                )}
              </View>
              {isLastSystem && timestamp && (
                <View className="mb-3 flex flex-row items-center gap-1 self-center">
                  <Icon
                    name="clock"
                    size={12}
                    className="text-gray-400 dark:text-gray-500"
                  />
                  <Text className="text-[11px] text-gray-400 dark:text-gray-500">{timestamp}</Text>
                </View>
              )}
            </View>
          );
        }
      })}
    </View>
  );
};
